import React, { useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import moment from 'moment'

import InlineGroup from 'components/Common/InlineGroup'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { pluralizeToString } from 'lib/string/pluralize'
import { DMY_CASUAL_SHORT_FORMAT, ISO_DATE_FORMAT } from 'constants/dateFormats'
import ProductTypeLabel from 'components/Luxkit/Label/ProductTypeLabel'
import { OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import OfferTileInclusions from 'components/OfferList/OfferListTiles/Support/OfferTileInclusions'
import TextButton from 'components/Luxkit/Button/TextButton'
import Group from 'components/utils/Group'
import Heading from 'components/Luxkit/Typography/Heading'
import TAOCancellationPolicies from 'components/Common/CancellationPolicies/AlwaysOn/TAOCancellationPolicies'
import { useOfferInclusions } from 'hooks/useOfferInclusions'
import AlwaysOnPriceDetails from 'components/OfferPage/AlwaysOn/AlwaysOnPriceDetails'
import { scrollToId } from 'lib/window/scrollUtils'
import { ValueWrapper } from 'components/Common/OfferPricing/styles'
import Pill from 'components/Luxkit/Pill'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import WalledContent from 'components/Common/WalledContent'
import { useAllInclusions } from 'hooks/useAllInclusions'
import { min } from 'lib/array/arrayUtils'

const TagWrapper = styled.div`
  display: flex;
  gap: ${rem(5)};

  ${mediaQueryUp.tablet} {
    flex-direction: column;
  }
`

const MainSection = styled.div`
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;

  ${mediaQueryUp.largeDesktop} {
    min-width: ${rem(340)};
  }

  > * + * {
    margin-top: ${rem(8)};
  }
`

const TravelPeriodSection = styled.div`
  display: flex;
  flex-direction: column;
`

const Wrapper = styled(InlineGroup)`
  width: 100%;
  min-width: 0;

  > * + * {
    margin-top: ${rem(6)};
  }

  flex-direction: column;
`

interface Props {
  offer: App.Offer;
  promotion: OfferPackageOptionView;
  checkIn: moment.Moment;
  rooms: Array<App.Occupants>;
  packagesWithIsBaseRate: { duration: number, value: number, packageId: string }[]
}

export const DetailSegment = styled.div`
  border: 1px solid ${({ theme }) => theme.palette.neutral.five};
  position: relative;
  padding: ${rem(20)} ${rem(24)};
  width: 100%;

  ${mediaQueryUp.desktop} {
    justify-content: space-between;
    display: grid;
    grid-template-columns: 60% 1fr;
    min-height: ${rem(260)};
  }
`

const LeftDetailSegment = styled.div`
  align-self: flex-start;

  > * + * {
    margin-top: ${rem(16)}
  }
`

const RightDetailSegment = styled.div`
  display: flex;
  align-self: flex-end;
  justify-content: flex-end;
  flex-direction: column;
  margin-top: ${rem(16)};
  position: relative;
  width: auto;
  min-width: ${rem(180)};

  ${mediaQueryUp.desktop} {
    margin-left: ${rem(20)};
  }
`

const StyledGroup = styled(Group)`
  > * + * {
    margin-left: ${rem(8)};
  }
`

const BuyButton = styled(TextButton)`
  margin-top: ${rem(16)};
`

function AlwaysOnOfferPromotion(props: Props) {
  const {
    offer,
    promotion,
    checkIn,
    rooms,
    packagesWithIsBaseRate,
  } = props

  const pkg = promotion.package

  const minDuration = useMemo(() => min(offer.packages.filter(p => p.hasTactical), p => p.duration)?.duration || 0, [offer.packages])

  // we send undefined for dates obviously because for intro we always should show possible inclusions
  const { inclusions: commonInclusions, bonusInclusions, tacticalInclusions } = useOfferInclusions(pkg, undefined, undefined)

  const inclusions = useAllInclusions(commonInclusions, bonusInclusions, tacticalInclusions)

  const shouldShowDiscountPercent = promotion.hotelDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const shouldShowBundledDiscountPercent = promotion.bundledDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const highestDiscountPercent = promotion.hotelDiscountPercent > promotion.bundledDiscountPercent ? promotion.hotelDiscountPercent : promotion.bundledDiscountPercent
  const shouldShowDiscountWrapper = !offer?.isDiscountPillHidden && (shouldShowDiscountPercent || shouldShowBundledDiscountPercent) && !pkg.roomRate?.inclusionsHideValue

  const basePackageDuration = useMemo(() => {
    return packagesWithIsBaseRate.find(
      (packageItem) => packageItem.duration === pkg.duration && packageItem.packageId === pkg.id,
    )
  }, [packagesWithIsBaseRate, pkg.duration, pkg.id])

  const baseRateCost = basePackageDuration?.value

  const valuedUpTo = useMemo(() => {
    const inclusionsAmount = pkg.inclusionsAmount ?? 0
    const propertyFees = pkg.propertyFees ?? 0
    if (baseRateCost && (baseRateCost + inclusionsAmount) > pkg.value) {
      const displayedHotelValue = baseRateCost + inclusionsAmount + propertyFees
      const displayedHotelDiscountPercent = getValueOffPercent(displayedHotelValue, promotion.hotelPrice)

      if (displayedHotelDiscountPercent > highestDiscountPercent) {
        return { value: displayedHotelValue, percent: displayedHotelDiscountPercent }
      }
    }

    return { value: promotion.hotelValue, percent: highestDiscountPercent }
  }, [pkg.inclusionsAmount, pkg.propertyFees, pkg.value, baseRateCost, promotion.hotelValue, promotion.hotelPrice, highestDiscountPercent])

  const { travelFrom, travelTo } = useMemo(() => {
    const tacticalSchedule = promotion?.schedules?.find(x => x.type === 'tactical')
    let travelFrom: string|undefined = tacticalSchedule?.travelPeriod?.from
    let travelTo: string|undefined = tacticalSchedule?.travelPeriod?.to
    if ((!travelFrom || !travelTo) && tacticalInclusions.length > 0) {
      const tacticalInclusionsSchedule = tacticalInclusions[0].schedules?.find(x => x.type === 'tactical')
      travelFrom = tacticalInclusionsSchedule?.travelPeriod?.from
      travelTo = tacticalInclusionsSchedule?.travelPeriod?.to
    }

    return { travelFrom, travelTo }
  }, [promotion?.schedules, tacticalInclusions])

  return (
    <Wrapper horizontal="start">
      <MainSection>
        <DetailSegment>
          <LeftDetailSegment>
            <StyledGroup direction="horizontal" verticalAlign="center" horizontalAlign="start">
              <ProductTypeLabel productType="limited_time_special"/>
            </StyledGroup>
            <Group direction="vertical" gap={8}>
              <VerticalSpacer gap={4}>
                <Heading variant="heading5">{pkg.name}</Heading>
                <TAOCancellationPolicies
                  offerId={offer.id}
                  useDynamicCancellationPolicies={offer.property?.useDynamicCancellationPolicies}
                  timezoneOffset={offer.property?.timezoneOffset}
                  uniqueKey={pkg.uniqueKey}
                  checkInDate={checkIn ? checkIn.format(ISO_DATE_FORMAT) : undefined}
                  checkOutDate={checkIn ? checkIn.clone().add(pkg.duration, 'days').format(ISO_DATE_FORMAT) : undefined}
                  occupants={rooms}
                  cancellationPolicy={pkg?.roomRate?.cancellationPolicy}
                />
              </VerticalSpacer>
              <OfferTileInclusions
                offerTitle={offer.property?.name ? offer.property.name : offer.name}
                heading="Your handpicked inclusions"
                tileInclusionsList={inclusions}
                maxInclusionsCount={3}
              />
            </Group>
            <TravelPeriodSection>
              <BodyText variant="small" weight="normal" colour="neutral-two">
                {travelFrom && travelTo && (
                  <>
                    Travel between: {moment(travelFrom).format(DMY_CASUAL_SHORT_FORMAT)} - {moment(travelTo).subtract(pkg.duration, 'days').format(DMY_CASUAL_SHORT_FORMAT)}.
                    {' '}
                  </>
                )}
                {!!minDuration &&
                  <>
                    Minimum stay: {pluralizeToString('night', minDuration)}
                  </>
                }
              </BodyText>
            </TravelPeriodSection>
          </LeftDetailSegment>
          <RightDetailSegment>
            <WalledContent enforced>
              <TagWrapper>
                {shouldShowDiscountWrapper &&
                  <ValueWrapper variant="medium" weight="bold">
                    <Pill kind="primary" variant="success">Save up to {valuedUpTo.percent}%</Pill>
                  </ValueWrapper>
                }
              </TagWrapper>
              <AlwaysOnPriceDetails
              rooms={rooms}
              checkIn={checkIn ? checkIn.format(ISO_DATE_FORMAT) : undefined}
              packageView={promotion}
              offer={offer}
              canBuyNow={false}
              valuedUpTo={valuedUpTo}
            />
            </WalledContent>
            <BuyButton
              kind="primary"
              data-filter="checkin"
              onClick={() => scrollToId(pkg?.roomType?.id ?? '')}
              fit="full-width"
            >
              Check availability
            </BuyButton>
          </RightDetailSegment>
        </DetailSegment>
      </MainSection>
    </Wrapper>
  )
}

export default AlwaysOnOfferPromotion
